import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface DriverStats {
  total_earnings: number;
  total_redemptions: number;
  active_coupons: number;
  vendors_recruited: number;
  commission_rate: number;
  this_month_earnings: number;
  this_week_earnings: number;
  recent_redemptions: Array<{
    id: number;
    coupon_title: string;
    vendor_name: string;
    commission_earned: number;
    redeemed_at: string;
  }>;
}

export const MobileStatsPage: React.FC = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState<DriverStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'all'>('month');

  useEffect(() => {
    fetchDriverStats();
  }, []);

  const fetchDriverStats = async () => {
    try {
      // Mock data for now - in real implementation, this would fetch from API
      const mockStats: DriverStats = {
        total_earnings: 1250.50,
        total_redemptions: 45,
        active_coupons: 8,
        vendors_recruited: 3,
        commission_rate: 15,
        this_month_earnings: 420.75,
        this_week_earnings: 89.25,
        recent_redemptions: [
          {
            id: 1,
            coupon_title: "Pizza 30% OFF",
            vendor_name: "Pizzaria do João",
            commission_earned: 15.50,
            redeemed_at: "2024-01-20T14:30:00Z"
          },
          {
            id: 2,
            coupon_title: "Desconto 20% Farmácia",
            vendor_name: "Farmácia Central",
            commission_earned: 8.25,
            redeemed_at: "2024-01-19T16:45:00Z"
          },
          {
            id: 3,
            coupon_title: "Café Grátis",
            vendor_name: "Café da Esquina",
            commission_earned: 12.00,
            redeemed_at: "2024-01-19T09:15:00Z"
          }
        ]
      };
      setStats(mockStats);
    } catch (error) {
      console.error('Failed to fetch driver stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getSelectedEarnings = () => {
    if (!stats) return 0;
    switch (selectedPeriod) {
      case 'week':
        return stats.this_week_earnings;
      case 'month':
        return stats.this_month_earnings;
      case 'all':
        return stats.total_earnings;
      default:
        return stats.this_month_earnings;
    }
  };

  const getEarningsLabel = () => {
    switch (selectedPeriod) {
      case 'week':
        return t('stats.earnings.week');
      case 'month':
        return t('stats.earnings.month');
      case 'all':
        return t('stats.earnings.total');
      default:
        return t('stats.earnings.month');
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('stats.loading')}</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('stats.error')}</h3>
          <p className="text-gray-600 mb-4">{t('stats.error.desc')}</p>
          <button
            onClick={fetchDriverStats}
            className="bg-green-600 text-white px-6 py-3 rounded-xl font-medium"
          >
            {t('stats.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white p-4 shadow-sm">
        <h1 className="text-xl font-bold text-gray-900">{t('stats.title')}</h1>
        <p className="text-gray-600 text-sm">{t('stats.subtitle')}</p>
      </div>

      {/* Period Filter */}
      <div className="bg-white p-4 border-b">
        <div className="flex bg-gray-100 rounded-xl p-1">
          {[
            { key: 'week', label: t('stats.period.week') },
            { key: 'month', label: t('stats.period.month') },
            { key: 'all', label: t('stats.period.all') }
          ].map((period) => (
            <button
              key={period.key}
              onClick={() => setSelectedPeriod(period.key as 'week' | 'month' | 'all')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                selectedPeriod === period.key
                  ? 'bg-white text-green-600 shadow-sm'
                  : 'text-gray-600'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Main Earnings Card */}
        <div className="p-4">
          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 text-white mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-green-100 text-sm">{getEarningsLabel()}</p>
                <p className="text-3xl font-bold">{formatCurrency(getSelectedEarnings())}</p>
              </div>
              <div className="text-4xl">💰</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span>{t('stats.commission.rate')}</span>
                <span className="font-bold">{stats.commission_rate}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="px-4 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xl">🎫</span>
                <span className="text-sm font-medium text-gray-600">{t('stats.active.coupons')}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.active_coupons}</p>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xl">✅</span>
                <span className="text-sm font-medium text-gray-600">{t('stats.total.redemptions')}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_redemptions}</p>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xl">🏪</span>
                <span className="text-sm font-medium text-gray-600">{t('stats.partners')}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.vendors_recruited}</p>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-xl">📈</span>
                <span className="text-sm font-medium text-gray-600">{t('stats.this.week')}</span>
              </div>
              <p className="text-xl font-bold text-green-600">{formatCurrency(stats.this_week_earnings)}</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="px-4 pb-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-bold text-gray-900">{t('stats.recent.activity')}</h3>
              <p className="text-sm text-gray-600">{t('stats.recent.desc')}</p>
            </div>
            <div className="divide-y">
              {stats.recent_redemptions.map((redemption) => (
                <div key={redemption.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">{redemption.coupon_title}</h4>
                      <p className="text-sm text-gray-600">{redemption.vendor_name}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(redemption.redeemed_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{formatCurrency(redemption.commission_earned)}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span className="text-xs text-green-600 font-medium">{t('stats.paid')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PIX Info */}
        <div className="px-4 pb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-xl">🏦</span>
              <span className="font-medium text-blue-900">{t('stats.pix.title').replace('🏦 ', '')}</span>
            </div>
            <p className="text-sm text-blue-800 mb-3">
              {t('stats.pix.desc')}
            </p>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
              {t('stats.pix.setup')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};